import { FC, useEffect, useRef, useState } from 'react'
import { CircularProgress, Typography } from '@mui/material'
import { useQuery } from 'react-query'
import theme from 'theme'

/* Utils */
import axios from '../../../../lib/axios'
import { downloadFile } from '../reports.utils'

/* Components */
import TabsHeader from '../../../components/tabs-header'
import { ThemeButton } from '../../../components/buttons'
import { ActionsWrapper, CustomizerContainer, FormItemWrapper, FormWrapper, Wrapper } from '../reports.styles'
import { DownloadIcon } from '../../../components/svg'
import { AllCPIsReportType } from '../../../../lib/utils'
import { closeToast, openToast, ToastVariant } from '../../../../store/toasts/toastSlice'
import { useAppDispatch } from '../../../../store/hooks'
import { CPITimePeriod } from 'models'
import { getPayloadTimePeriod } from 'ui/pages/library/utils'
import ReportsSelectField from '../customize-report/components/ReportsSelectField'

// Time period configuration - single source of truth
const TIME_PERIODS = [
  { value: CPITimePeriod.Week, label: 'Past 1 Week' },
  { value: CPITimePeriod.Month, label: 'Past 1 Month' },
  { value: CPITimePeriod.ThreeMonths, label: 'Past 3 Months' },
  { value: CPITimePeriod.SixMonths, label: 'Past 6 Months' },
  { value: CPITimePeriod.PastYear, label: 'Past 1 Year' }
]

const getLabel = (value: CPITimePeriod) => TIME_PERIODS.find((p) => p.value === value)?.label || value

const getValue = (label: string) => TIME_PERIODS.find((p) => p.label === label)?.value || label

const FullReportBuilder: FC = () => {
  const dispatch = useAppDispatch()
  const closeNotification = useRef<any>(null)
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<CPITimePeriod | string>('')
  const [generatingCpiReport, setGeneratingCpiReport] = useState(false)
  const [code, setCode] = useState('')
  const [isPolling, setIsPolling] = useState(false)

  const { data, error, isFetching } = useQuery(
    ['code', { code }],
    () => axios.get(`/api/v1/reports/download_url/${code}`),
    {
      refetchInterval: 5000,
      enabled: !!code
    }
  )

  useEffect(() => {
    if (error && (error as any).response?.status === 418) {
      setGeneratingCpiReport(false)
      setCode('')
      setIsPolling(false)
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: (error as any).response?.data.error || 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    }
  }, [error])

  useEffect(() => {
    if (data && !error && !isFetching) {
      const downloadUrl = data.data.reportDownloadUrl.url
      downloadFile(`${downloadUrl}`, 'Full Report')

      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      setCode('')
      setIsPolling(false)
    }
  }, [data, error, isFetching, code])

  const generateFullReport = async () => {
    setGeneratingCpiReport(true)

    try {
      const payload = {
        timePeriod: getPayloadTimePeriod(selectedTimePeriod as CPITimePeriod),
        type: AllCPIsReportType.GENERAL
      }

      const res = await axios.post(`/api/v1/reports/all_cpis`, payload)

      if (res.status.toString().startsWith('2')) {
        const { code } = res.data
        setCode(code)
        setIsPolling(true)
      }
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
    } finally {
      setGeneratingCpiReport(false)
    }
  }

  return (
    <CustomizerContainer>
      <TabsHeader
        tabValue={0}
        parents={['Reports']}
        handleTabChange={() => {
          //
        }}
        title="Customize a Full Report"
        tabLabels={[]}
        hideIntegrationsTabs={true}
      />

      <Wrapper>
        <FormWrapper>
          <FormItemWrapper>
            <Typography className="title">Select the date range:</Typography>
            <ReportsSelectField
              options={TIME_PERIODS.map((timePeriod) => getLabel(timePeriod.value))}
              value={getLabel(selectedTimePeriod as CPITimePeriod)}
              handleChange={(e: any) => setSelectedTimePeriod(getValue(e.target.value))}
            />
          </FormItemWrapper>
        </FormWrapper>
        <ActionsWrapper sx={{ marginBottom: '50px' }}>
          <ThemeButton
            onClick={generateFullReport}
            disabled={!selectedTimePeriod || generatingCpiReport || isFetching || isPolling}
          >
            <>
              {generatingCpiReport || isFetching || isPolling ? (
                <CircularProgress size={18} color="secondary" />
              ) : (
                <DownloadIcon
                  fill={!selectedTimePeriod ? theme.baseColors.greyscale[80] : theme.baseColors.primary[20]}
                />
              )}
              Download PDF
            </>
          </ThemeButton>
        </ActionsWrapper>
      </Wrapper>
    </CustomizerContainer>
  )
}

export default FullReportBuilder
